import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Scale, Trash2 } from 'lucide-react';

const faqs = [
  {
    q: '¿Qué es la merma en cocina?',
    a: 'La merma es la pérdida de peso o volumen que sufre un ingrediente durante su manipulación: limpieza, pelado, desespinado, cocción, etc. Por ejemplo, un kilo de gambas frescas con cabeza puede quedarse en 400-500 g de producto aprovechable tras pelarlas.',
  },
  {
    q: '¿Cómo se calcula la merma de un ingrediente?',
    a: 'Merma (%) = [(Peso bruto - Peso neto) / Peso bruto] × 100. Ejemplo: si compras 1 kg de alcachofas y tras limpiarlas te quedan 500 g, la merma es del 50%. Esto significa que el coste real por kilo aprovechable es el doble del precio de compra.',
  },
  {
    q: '¿Cuánta merma es aceptable en un restaurante?',
    a: 'La merma total aceptable en un restaurante bien gestionado no debería superar el 5% del coste total de insumos. Las mermas de cada ingrediente individual varían mucho: desde 0% (aceite, sal) hasta un 60% (gambas con cabeza, alcachofas).',
  },
  {
    q: '¿Cuál es la diferencia entre merma y desperdicio?',
    a: 'La merma es inevitable (cáscaras, huesos, espinas) y se puede minimizar pero no eliminar. El desperdicio es evitable: producto caducado, mala conservación, errores de preparación. Un buen restaurante minimiza ambos, pero el desperdicio es donde más dinero se pierde.',
  },
  {
    q: '¿Cómo ayuda PLEKO a controlar las mermas?',
    a: 'PLEKO permite configurar el porcentaje de merma de cada ingrediente (limpieza y cocción por separado). Al crear una receta, el sistema calcula automáticamente el coste real teniendo en cuenta la merma, para que el food cost refleje el coste verdadero del plato.',
  },
];

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const mermasLimpieza = [
  { ingrediente: 'Alcachofas', merma: '45-55%', motivo: 'Hojas externas, tallo, pelusa' },
  { ingrediente: 'Gambas con cabeza', merma: '50-60%', motivo: 'Cabeza, caparazón, intestino' },
  { ingrediente: 'Merluza entera', merma: '40-50%', motivo: 'Cabeza, espinas, piel, vísceras' },
  { ingrediente: 'Cebolla', merma: '10-15%', motivo: 'Piel seca, raíces, puntas' },
  { ingrediente: 'Puerro', merma: '35-45%', motivo: 'Hojas verdes externas, raíces' },
  { ingrediente: 'Patatas', merma: '15-20%', motivo: 'Piel, ojos, zonas verdes' },
  { ingrediente: 'Pimientos', merma: '15-20%', motivo: 'Semillas, pedúnculo, nervios' },
  { ingrediente: 'Pollo entero', merma: '25-35%', motivo: 'Huesos, piel, grasa' },
];

const mermasCoccion = [
  { ingrediente: 'Carne de vacuno', merma: '25-35%', motivo: 'Pierde agua y grasa' },
  { ingrediente: 'Pollo a la plancha', merma: '20-25%', motivo: 'Pierde agua' },
  { ingrediente: 'Verduras salteadas', merma: '15-25%', motivo: 'Evaporación de agua' },
  { ingrediente: 'Pescado al horno', merma: '15-20%', motivo: 'Pierde agua y grasa' },
  { ingrediente: 'Arroz (hervido)', merma: '-150%', motivo: 'Absorbe agua: gana peso' },
  { ingrediente: 'Pasta seca', merma: '-100%', motivo: 'Absorbe agua: duplica peso' },
  { ingrediente: 'Legumbres secas', merma: '-120%', motivo: 'Absorben agua' },
  { ingrediente: 'Setas', merma: '30-40%', motivo: 'Alto contenido en agua' },
];

export default function MermasCocinaPage() {
  return (
    <>
      <Helmet>
        <title>Mermas en Cocina: Qué Son, Cómo Calcularlas y Reducirlas [2026] | PLEKO</title>
        <meta
          name="description"
          content="Guía completa de mermas en cocina profesional. Tablas de merma por ingrediente, fórmulas de cálculo, diferencia entre merma y desperdicio, y cómo controlarlas con software."
        />
        <meta name="keywords" content="merma cocina, mermas restaurante, calcular merma ingredientes, tabla mermas cocina, reducir mermas restaurante, desperdicios cocina, control mermas hostelería" />
        <link rel="canonical" href="https://pleko.app/mermas-cocina" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pleko.app/mermas-cocina" />
        <meta property="og:title" content="Mermas en Cocina: Qué Son, Cómo Calcularlas y Reducirlas [2026] | PLEKO" />
        <meta property="og:description" content="Guía completa de mermas en cocina profesional. Tablas de merma por ingrediente, fórmulas de cálculo, diferencia entre merma y desperdicio, y cómo controlarlas con software." />
        <meta property="og:image" content="https://pleko.app/og-image.png" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Mermas en Cocina: Qué Son, Cómo Calcularlas y Reducirlas [2026] | PLEKO" />
        <meta name="twitter:description" content="Guía completa de mermas en cocina profesional. Tablas de merma por ingrediente, fórmulas de cálculo, diferencia entre merma y desperdicio, y cómo controlarlas con software." />
        <meta name="twitter:image" content="https://pleko.app/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-b from-amber-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-6">
              <Scale className="w-4 h-4" />
              Guía de mermas en cocina
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
              Mermas en cocina:<br />
              <span className="text-amber-500">cómo calcularlas y reducirlas</span>
            </h1>
            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
              Las mermas no contabilizadas pueden inflarte el food cost un 10-15% sin que lo sepas.
              Aprende a medirlas y controlarlas para proteger tu margen.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-white font-semibold rounded-xl hover:bg-amber-600 transition-colors"
            >
              Controlar mermas con PLEKO
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Content */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="prose prose-lg max-w-none">

            <h2 className="text-3xl font-bold text-slate-900 mb-6">¿Qué es la merma en cocina?</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La <strong>merma</strong> es la diferencia entre el peso bruto de un ingrediente (lo que compras) y el peso neto (lo que realmente aprovechas en el plato). Incluye todo lo que se descarta durante la limpieza, el pelado, el desespinado y la cocción.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              Si no contabilizas las mermas en tus escandallos, estás <strong>infravalorando el coste real de cada plato</strong>. Un kilo de gambas a 18€/kg que tiene un 55% de merma en realidad te cuesta 40€/kg de producto aprovechable.
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-10 text-center">
              <p className="font-bold text-amber-800 mb-2">Fórmula de la merma</p>
              <p className="text-2xl font-bold text-amber-600 mb-2">Merma (%) = [(Peso bruto − Peso neto) / Peso bruto] × 100</p>
              <p className="text-slate-600 mt-2">
                Ejemplo: 1 kg de alcachofas → 500 g limpias = 50% de merma
              </p>
              <p className="text-lg font-semibold text-amber-700 mt-4">
                Coste real/kg = Precio compra/kg ÷ (1 − Merma/100)
              </p>
              <p className="text-slate-600 mt-1">
                Alcachofas a 4€/kg con 50% merma → Coste real = 8€/kg aprovechable
              </p>
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mb-6">Merma vs. desperdicio: no es lo mismo</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              <div className="border border-amber-200 bg-amber-50 rounded-xl p-5">
                <h3 className="font-bold text-amber-700 mb-3 flex items-center gap-2">
                  <Scale className="w-5 h-5" /> Merma (inevitable)
                </h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  {[
                    'Cáscaras, pieles, huesos, espinas',
                    'Pérdida de peso en cocción',
                    'Partes no comestibles del ingrediente',
                    'Se minimiza con técnica, pero no se elimina',
                    'Se contabiliza en el escandallo',
                  ].map((i) => <li key={i} className="flex gap-2"><span className="text-amber-500">•</span>{i}</li>)}
                </ul>
              </div>
              <div className="border border-red-200 bg-red-50 rounded-xl p-5">
                <h3 className="font-bold text-red-700 mb-3 flex items-center gap-2">
                  <Trash2 className="w-5 h-5" /> Desperdicio (evitable)
                </h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  {[
                    'Producto caducado o en mal estado',
                    'Sobreproducción no vendida',
                    'Errores de preparación o porciones excesivas',
                    'Mala rotación de stock (no usar FIFO)',
                    'Se elimina con buena gestión',
                  ].map((i) => <li key={i} className="flex gap-2"><span className="text-red-500">•</span>{i}</li>)}
                </ul>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mb-6">Tabla de mermas en limpieza (en crudo)</h2>
            <p className="text-slate-600 mb-4">Porcentajes habituales en cocina profesional española. Varían según calidad del producto y proveedor.</p>
            <div className="overflow-x-auto mb-10">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-amber-700 text-white">
                    <th className="p-3 text-left font-medium">Ingrediente</th>
                    <th className="p-3 text-center font-medium">Merma</th>
                    <th className="p-3 text-left font-medium">Causa principal</th>
                  </tr>
                </thead>
                <tbody>
                  {mermasLimpieza.map(({ ingrediente, merma, motivo }, i) => (
                    <tr key={ingrediente} className={i % 2 === 0 ? 'bg-white' : 'bg-amber-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ingrediente}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-bold text-red-600">{merma}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{motivo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mb-6">Tabla de mermas en cocción</h2>
            <p className="text-slate-600 mb-4">Variación de peso tras la cocción. Los valores negativos indican que el ingrediente gana peso (absorbe agua).</p>
            <div className="overflow-x-auto mb-10">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-orange-700 text-white">
                    <th className="p-3 text-left font-medium">Ingrediente</th>
                    <th className="p-3 text-center font-medium">Variación</th>
                    <th className="p-3 text-left font-medium">Nota</th>
                  </tr>
                </thead>
                <tbody>
                  {mermasCoccion.map(({ ingrediente, merma, motivo }, i) => (
                    <tr key={ingrediente} className={i % 2 === 0 ? 'bg-white' : 'bg-orange-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ingrediente}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-bold text-orange-600">{merma}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{motivo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mb-6">Cómo reducir las mermas en tu restaurante</h2>
            <div className="space-y-4 mb-10">
              {[
                {
                  num: '1',
                  title: 'Compra producto ya limpio cuando compense',
                  desc: 'A veces el pescado fileteado o la verdura cortada es más caro por kilo, pero al eliminar la merma el coste real puede ser similar o incluso menor.',
                },
                {
                  num: '2',
                  title: 'Aprovecha los recortes',
                  desc: 'Huesos para fondos, cabezas de pescado para fumet, pieles para chips, tallos para cremas. Cada recorte aprovechado reduce tu merma efectiva.',
                },
                {
                  num: '3',
                  title: 'Forma a tu equipo en técnicas de corte',
                  desc: 'Un cocinero experimentado saca más producto neto del mismo ingrediente. La diferencia entre un pelado eficiente y uno torpe puede ser un 10% de merma adicional.',
                },
                {
                  num: '4',
                  title: 'Mide y registra las mermas reales',
                  desc: 'Los porcentajes de las tablas son orientativos. Pesa tus ingredientes antes y después de limpiarlos para obtener tus mermas reales según tu producto y proveedor.',
                },
                {
                  num: '5',
                  title: 'Automatiza el control con software',
                  desc: 'PLEKO permite configurar la merma de limpieza y cocción de cada ingrediente. Al crear un escandallo, el coste real se calcula automáticamente.',
                },
              ].map(({ num, title, desc }) => (
                <div key={num} className="flex gap-4 p-5 bg-slate-50 rounded-xl border border-slate-200">
                  <span className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 text-sm">{num}</span>
                  <div>
                    <p className="font-semibold text-slate-800 mb-1">{title}</p>
                    <p className="text-slate-600 text-sm">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mb-6">Impacto de las mermas en el food cost</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Veamos un ejemplo concreto: una receta de <strong>lubina al horno</strong> que usa 300 g de lubina entera por ración.
            </p>
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              <div className="border border-red-200 rounded-xl p-5">
                <h3 className="font-bold text-red-700 mb-3">Sin contabilizar merma</h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li>Lubina: 300 g × 22€/kg = <strong>6,60€</strong></li>
                  <li>Food cost aparente: 6,60€ / 24€ PVP = <strong>27,5%</strong></li>
                  <li className="text-green-600 font-medium">Parece rentable</li>
                </ul>
              </div>
              <div className="border border-green-200 rounded-xl p-5">
                <h3 className="font-bold text-green-700 mb-3">Con merma del 45%</h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li>Necesitas comprar: 300 g / 0,55 = <strong>545 g</strong></li>
                  <li>Coste real: 545 g × 22€/kg = <strong>12,00€</strong></li>
                  <li>Food cost real: 12,00€ / 24€ PVP = <strong>50%</strong></li>
                  <li className="text-red-600 font-medium">Estás perdiendo dinero</li>
                </ul>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Preguntas frecuentes sobre mermas</h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl p-8 text-center text-white">
            <Scale className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">Controla las mermas y protege tu margen</h2>
            <p className="text-amber-100 mb-6 max-w-xl mx-auto">
              PLEKO calcula el coste real de cada plato con mermas incluidas. Sin sorpresas en el food cost.
              14 días gratis, sin tarjeta de crédito.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-amber-600 font-bold rounded-xl hover:bg-amber-50 transition-colors"
            >
              Empezar gratis
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
